import type { ModelMixRow } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { ArrowRight, Info, Lightbulb, PieChart, Shuffle } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { EmptyState, Panel, Pressable, Segmented, Select, Tooltip, formatPercent } from '@orgus/ui';
import { formatTokens } from '../costs/model.ts';
import { useCosts } from '../costs/useCosts.ts';
import { whatIf } from './model.ts';
import type { WhatIfMove } from './model.ts';
import { ReportGate, ReportSkeleton, ScopeBar, Tile, useScope } from './Scope.tsx';
import { useFinanceReport } from './useFinance.ts';

type Rates = NonNullable<ModelMixRow['rates']>;
type ShareKey = 'light' | '0.1' | '0.25' | '0.5' | '1';

function blended(r: Rates): number {
  return r.input + r.output;
}

/** The smallest share drawn as a bar; below it a fill is a dot on an empty track. */
const MIN_BAR_SHARE = 0.02;

function Mix({ mix }: { mix: ModelMixRow[] }) {
  const total = mix.reduce((a, m) => a + m.cost, 0);
  const SERIES = ['var(--hue-1)', 'var(--hue-3)', 'var(--hue-5)', 'var(--hue-7)', 'var(--hue-2)', 'var(--hue-8)'];
  return (
    <ul className="flex flex-col gap-3">
      {mix.map((m, i) => {
        const tok = m.tokens.input + m.tokens.output + m.tokens.cacheRead + m.tokens.cacheWrite;
        const share = total > 0 ? m.cost / total : 0;
        return (
          <li key={m.model} className="flex flex-col gap-1.5">
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-base text-fg">{m.label}</span>
              <span className="shrink-0 text-base font-medium text-fg num">{formatUsd(m.cost)}</span>
            </span>
            {/* A share too small to draw (under 2%) shows no bar, only its percent: an empty
                track, or a lone dot at its start, reads as broken. */}
            {share >= MIN_BAR_SHARE && (
              <span className="relative h-1.5 overflow-hidden rounded-full bg-active" aria-hidden>
                <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${share * 100}%`, background: SERIES[i % SERIES.length] }} />
              </span>
            )}
            {/* One fact on a phone; tokens and light work join it from md up. */}
            <span className="flex min-w-0 gap-3 text-caption text-fg-subtle num">
              <span className="shrink-0">{formatPercent(share)} of spend</span>
              <span className="hidden truncate md:inline">{formatTokens(tok)}</span>
              <span className="hidden truncate md:inline">{formatPercent(m.lightShare)} light work</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function WhatIf({ mix, rates, labels }: { mix: ModelMixRow[]; rates: Record<string, Rates>; labels: Record<string, string> }) {
  const priced = mix.filter((m) => m.rates);
  const [from, setFrom] = useState(priced[0]?.model ?? '');
  const fromRow = priced.find((m) => m.model === from);
  // Cheaper models in price-list order (newest first), so the first pick is a current model.
  const targets = Object.entries(rates).filter(([k, r]) => k !== from && fromRow?.rates && blended(r) < blended(fromRow.rates));
  const [to, setTo] = useState('');
  const target = targets.find(([k]) => k === to)?.[0] ?? targets[0]?.[0] ?? '';
  const [shareKey, setShareKey] = useState<ShareKey>((priced[0]?.lightShare ?? 0) > 0 ? 'light' : '0.25');
  const share = shareKey === 'light' ? (fromRow?.lightShare ?? 0) : Number(shareKey);
  const move: WhatIfMove = { from, to: target, share };
  const res = whatIf(mix, move, rates);

  const suggestions = useMemo(() => {
    const out: { from: string; to: string; saved: number }[] = [];
    for (const m of priced) {
      const cheaper = Object.entries(rates).filter(([k, r]) => k !== m.model && blended(r) < blended(m.rates!) && blended(r) >= blended(m.rates!) / 25);
      let best: { to: string; saved: number } | null = null;
      for (const [k] of cheaper) {
        const r = whatIf(mix, { from: m.model, to: k, share: m.lightShare }, rates);
        if (r && (!best || r.saved > best.saved)) best = { to: k, saved: r.saved };
      }
      if (best && best.saved >= 0.01) out.push({ from: m.model, ...best });
    }
    return out.sort((a, b) => b.saved - a.saved).slice(0, 3);
  }, [priced, rates, mix]);

  const shareOptions = [
    { value: 'light' as const, label: `Light work, ${formatPercent(fromRow?.lightShare ?? 0)}` },
    { value: '0.1' as const, label: '10%' },
    { value: '0.25' as const, label: '25%' },
    { value: '0.5' as const, label: '50%' },
    { value: '1' as const, label: 'All' },
  ];
  if (!priced.length) {
    return <EmptyState icon={<Shuffle />} title="Nothing to compare yet" description="Savings from cheaper models appear once sessions spend tokens on a priced model." />;
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 items-end gap-3 md:flex md:flex-wrap">
        <Select
          label="Move work from"
          options={priced.map((m) => ({ value: m.model, label: m.label }))}
          value={from}
          onValueChange={(v) => {
            setFrom(v);
            setTo('');
            if (!(priced.find((m) => m.model === v)?.lightShare ?? 0)) setShareKey((k) => (k === 'light' ? '0.25' : k));
          }}
          wrapperClassName="min-w-0 md:w-52"
        />
        {/* The arrow only reads when From and To sit side by side. */}
        <ArrowRight className="mb-2 hidden size-4 text-fg-subtle md:block" aria-hidden />
        <Select
          label="To"
          options={targets.length ? targets.map(([k]) => ({ value: k, label: labels[k] ?? k })) : [{ value: '', label: 'No cheaper model', disabled: true }]}
          value={target}
          onValueChange={setTo}
          wrapperClassName="min-w-0 md:w-52"
        />
        <div className="col-span-2 flex flex-col gap-1.5">
          <span className="text-ui font-medium text-fg-muted">How much of it</span>
          <div className="hidden md:block">
            <Segmented<ShareKey> label="How much of the work moves" size="md" options={shareOptions} value={shareKey} onChange={setShareKey} />
          </div>
          <Select aria-label="How much of the work moves" options={shareOptions} value={shareKey} onValueChange={(v) => setShareKey(v as ShareKey)} wrapperClassName="md:hidden" />
        </div>
      </div>
      {res ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Tile label="That work costs now" value={formatUsd(res.before)} sub={`${formatPercent(share)} of ${labels[from] ?? from} tokens`} />
          <Tile label={`On ${labels[target] ?? target}`} value={formatUsd(res.after)} sub="Same tokens at its list price" />
          <Tile label="You would save" value={formatUsd(res.saved)} sub={`${formatPercent(res.savedShare, 1)} of token spend`} />
        </div>
      ) : (
        <p className="text-sm text-fg-muted">Pick a cheaper model to compare.</p>
      )}
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-sm font-medium text-fg">
            <Lightbulb className="size-4 text-fg-subtle" aria-hidden /> Biggest savings from light work
          </span>
          <ul className="flex flex-col">
            {suggestions.map((s) => (
              <li key={`${s.from}>${s.to}`}>
                <Pressable
                  className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-hover"
                  onClick={() => {
                    setFrom(s.from);
                    setTo(s.to);
                    setShareKey('light');
                  }}
                  aria-label={`Compare routing light ${labels[s.from] ?? s.from} work to ${labels[s.to] ?? s.to}`}
                >
                  <span className="min-w-0 text-fg-muted">
                    Route light {labels[s.from] ?? s.from} work to {labels[s.to] ?? s.to}
                  </span>
                  <span className="shrink-0 whitespace-nowrap font-medium text-fg num">save {formatUsd(s.saved)}</span>
                </Pressable>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="flex items-center gap-2 text-caption text-fg-subtle">
        <Info className="size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 truncate">An estimate at list price</span>
        <Tooltip content="The same token counts at the other model's current list price. Light work is sessions with short answers over few calls. A cheaper model may need more turns or a different tokenizer, so try it on a few missions before routing by default.">
          <span tabIndex={0} className="shrink-0 rounded-control underline decoration-dotted underline-offset-2">
            How it is estimated
          </span>
        </Tooltip>
      </p>
    </div>
  );
}

/** Spend share per model, and what routing simple work to a cheaper model would save. */
export function ModelMixView() {
  const scope = useScope();
  const result = useFinanceReport(scope);
  const costs = useCosts();
  const { rates, labels } = useMemo(() => {
    const r: Record<string, Rates> = {};
    const l: Record<string, string> = {};
    for (const p of costs?.pricing.prices ?? []) {
      r[p.match] = { input: p.input, output: p.output, cacheRead: p.cacheRead, cacheWrite: p.cacheWrite, cacheWrite1h: p.cacheWrite1h };
      l[p.match] = p.label;
    }
    return { rates: r, labels: l };
  }, [costs?.pricing]);
  return (
    <>
      <ScopeBar scope={scope} result={result} />
      <ReportGate result={result} skeleton={<ReportSkeleton tiles={3} />}>
        {(r) => {
          const allLabels = { ...labels, ...Object.fromEntries(r.modelMix.map((m) => [m.model, m.label])) };
          return r.modelMix.length === 0 ? (
            <Panel>
              <EmptyState size="page" icon={<PieChart />} title="No token spend in this period" description="Pick a longer period or another project." />
            </Panel>
          ) : (
            <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <Panel title="Spend per model" sub={formatUsd(r.byCategory.tokens)} icon={<PieChart />}>
                <Mix mix={r.modelMix} />
              </Panel>
              <Panel title="What if" sub={<span className="hidden md:inline">Route work to a cheaper model</span>} icon={<Shuffle />}>
                <WhatIf key={`${r.from}|${r.to}|${r.projectId ?? ''}`} mix={r.modelMix} rates={rates} labels={allLabels} />
              </Panel>
            </div>
          );
        }}
      </ReportGate>
    </>
  );
}
